// src/components/ParentTeacher/ParentTeacherPortal.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import drIsInLogo from "../../assets/dr-is-in.png";
import caseyImage from "../../assets/Casey_2.png";
import AvatarGalleryModal, { AvatarItem } from "../Student/AvatarGalleryModal";
import VideoRoom from "../Realtime/VideoRoom";
import api from "../../api";
import {
  LANGUAGE_OPTIONS,
  getLanguageCode,
  isRtlLanguage,
} from "../../config/languages";
import "./ParentTeacherPortal.css";
import AuraSosLauncher, {
  AuraSosActivationDraft,
} from "../AuraERH/AuraSosLauncher";

type DoctorLite = {
  id: number;
  name: string;
  specialties?: string | null;
  medical_field?: string | null;
  is_online?: boolean;
  is_available?: boolean;
  status?: string | null;
  email?: string | null;
  photo_url?: string | null;
};

type TipLite = {
  id: number | string;
  category?: string | null;
  title?: string | null;
  body?: string | null;
  status?: string | null;
  image_url?: string | null;
  author_name?: string | null;
  author_role?: string | null;
};

type TipCategory = "mental" | "physical" | "everyday";

type InviteMode = "chat" | "video";
type VideoRoomRole = "teacher" | "parent" | "pt";

type LiveMsg = {
  id: string;
  from: "pt" | "doctor" | "system";
  text: string;
  ts?: number;
};

type AiChatResponse = {
  reply?: string;
  trigger_level?: string;
  escalation?: boolean;
  pt_id?: any;
  pt_role?: string;
  session_id?: string;
  analysis?: Record<string, any>;
  classification?: Record<string, any>;
  metadata?: Record<string, any>;
  state?: Record<string, any>;
  conversation_state?: Record<string, any>;
  actions?: string[];
  safety_plan?: string[];
};

type PortalAuthState = {
  token: string;
  ptCode: string;
  ptPk: string;
  ptRole: string;
  orgCodeRaw: string;
  orgCode: string;
  orgId: number;
  displayName: string;
  avatarId: string;
  avatarSrc: string;
};

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com"
).replace(/\/+$/, "");
const STORES: Storage[] = [sessionStorage, localStorage];
const PT_PORTAL_ROUTE = "/pt-portal";
const PT_PORTAL_RETURN_KEY = "pt_portal_return";

const PT_TIP_CATEGORIES: Array<{ value: TipCategory; label: string }> = [
  { value: "mental", label: "Mental wellness" },
  { value: "physical", label: "Physical wellness" },
  { value: "everyday", label: "Everyday support" },
];

function clean(v: any) {
  return String(v ?? "").trim();
}

function normOrgCode(v: string) {
  return clean(v)
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase();
}


function inferPtAudience(ptRole?: string, ptCode?: string): "parent" | "teacher" {
  const role = clean(ptRole).toLowerCase();
  const code = clean(ptCode).toUpperCase();

  if (role.includes("teacher") || code.startsWith("T")) return "teacher";
  if (role.includes("parent") || code.startsWith("P")) return "parent";

  return "parent";
}

function displayPtRole(ptRole?: string, ptCode?: string) {
  return inferPtAudience(ptRole, ptCode) === "teacher" ? "Teacher" : "Parent";
}

function makePtPresenceKey(orgCode?: string, ptCode?: string, ptPk?: string, ptRole?: string) {
  const audience = inferPtAudience(ptRole, ptCode);
  const org = normOrgCode(clean(orgCode)) || "ORG";
  const code = clean(ptCode).toUpperCase() || clean(ptPk) || "PT";
  return `${audience}:${org}:${code}`;
}

function normalizeBearer(t: string) {
  return (t || "").replace(/^Bearer\s+/i, "").trim();
}

function resolveUrl(url?: string | null) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = API_BASE.replace(/\/+$/, "");
  const slash = url.startsWith("/") ? "" : "/";
  return `${base}${slash}${url}`;
}

function speakCasey(text: string) {
  try {
    const synth = window.speechSynthesis;
    if (!synth || !text) return;
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.96;
    utter.pitch = 1.0;
    utter.volume = 1.0;
    synth.speak(utter);
  } catch {}
}

function getFirst(keys: string[]): string {
  for (const store of STORES) {
    for (const k of keys) {
      try {
        const v = store.getItem(k);
        if (v && String(v).trim()) return String(v).trim();
      } catch {}
    }
  }
  return "";
}

function setPortalReturn(path: string) {
  const v = clean(path) || PT_PORTAL_ROUTE;
  try {
    sessionStorage.setItem(PT_PORTAL_RETURN_KEY, v);
  } catch {}
}

function makeClientMsgId() {
  return `m_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function makeCaseySessionId(
  kind: "pt" | "student" | "parent" | "teacher",
  principal: string,
  orgCode?: string,
) {
  const p = clean(principal).replace(/[^A-Za-z0-9_-]/g, "") || "anon";
  const o = clean(orgCode).replace(/[^A-Za-z0-9_-]/g, "") || "org";
  return `${kind}:${o}:${p}`;
}

function getStoredCaseySessionId(storageKey: string, fallbackId: string) {
  try {
    const s1 = sessionStorage.getItem(storageKey);
    if (clean(s1)) return clean(s1);
  } catch {}
  try {
    const s2 = localStorage.getItem(storageKey);
    if (clean(s2)) return clean(s2);
  } catch {}
  try {
    sessionStorage.setItem(storageKey, fallbackId);
  } catch {}
  try {
    localStorage.setItem(storageKey, fallbackId);
  } catch {}
  return fallbackId;
}

function persistCaseySessionId(storageKey: string, sessionId: string) {
  const v = clean(sessionId);
  if (!v) return;
  try {
    sessionStorage.setItem(storageKey, v);
  } catch {}
  try {
    localStorage.setItem(storageKey, v);
  } catch {}
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function typeCaseyMessage(
  text: string,
  setMessages: React.Dispatch<
    React.SetStateAction<
      Array<{ id: string; from: "pt" | "system"; text: string; ts: number }>
    >
  >,
  mode: string = "supportive_conversation",
) {
  const id = makeClientMsgId();

  setMessages((prev) => [
    ...prev,
    {
      id,
      from: "system",
      text: "Casey is typing...",
      ts: Date.now(),
    },
  ]);

  let baseDelay = 800;
  if (mode === "emergency_instruction") baseDelay = 120;
  if (mode === "immediate_safety_check") baseDelay = 250;
  if (mode === "immediate_danger") baseDelay = 160;
  if (mode === "high_risk") baseDelay = 220;
  if (mode === "critical") baseDelay = 200;
  if (mode === "high") baseDelay = 240;

  await sleep(baseDelay);

  let current = "";

  const updateTypedMessage = (nextText: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, text: nextText } : m)),
    );
  };

  for (let i = 0; i < text.length; i++) {
    current += text[i];
    updateTypedMessage(current);

    let delay = 12;
    if (mode === "emergency_instruction") delay = 2;
    if (mode === "immediate_danger") delay = 4;
    if (mode === "high_risk") delay = 6;

    if ([".", "!", "?"].includes(text[i])) delay += 40;
    if ([",", ";"].includes(text[i])) delay += 20;

    await sleep(delay);
  }
}

function getEventType(data: any) {
  return clean(
    data?.type ||
      data?.event ||
      data?.action ||
      data?.payload?.type ||
      data?.payload?.event,
  ).toLowerCase();
}

function isPresenceSnapshot(data: any) {
  const t = getEventType(data);
  return !!(
    data &&
    (t === "presence" ||
      t === "presence.snapshot" ||
      !!data.online ||
      Array.isArray(data.doctors))
  );
}

function isChatMessage(data: any) {
  const t = getEventType(data);
  return !!(
    data &&
    (t === "chat.message" ||
      t === "message" ||
      t === "chat" ||
      data?.payload?.message ||
      data?.payload?.text ||
      data?.message ||
      data?.text)
  );
}

function isInviteAccept(data: any) {
  const t = getEventType(data);
  const status = clean(data?.status || data?.payload?.status).toLowerCase();
  const action = clean(data?.action || data?.payload?.action).toLowerCase();
  return (
    t === "invite.accept" ||
    t === "invite.accepted" ||
    t === "invite.accept.ack" ||
    t === "invite_ack" ||
    status === "accepted" ||
    action === "accept"
  );
}

function isInviteDecline(data: any) {
  const t = getEventType(data);
  const status = clean(data?.status || data?.payload?.status).toLowerCase();
  const action = clean(data?.action || data?.payload?.action).toLowerCase();
  return (
    t === "invite.decline" ||
    t === "invite.declined" ||
    t === "invite.decline.ack" ||
    status === "declined" ||
    action === "decline"
  );
}

function makeRoomIdFromPk(ptPk: string, doctorId: number | string) {
  const safePt = clean(ptPk).replace(/[^A-Za-z0-9_-]/g, "") || "pt";
  const safeDoctor =
    clean(String(doctorId)).replace(/[^A-Za-z0-9_-]/g, "") || "doctor";
  return `dm:PT:${safePt}:${safeDoctor}`;
}

function buildPtWsUrl(
  apiBase: string,
  ptPk: string,
  opts?: {
    display_name?: string;
    org_code?: string;
    pt_code?: string;
    pt_role?: string;
    avatar_id?: string;
    avatar_src?: string;
    token?: string;
  },
) {
  const base = (apiBase || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
  const wsBase = base.startsWith("https://")
    ? base.replace(/^https:/, "wss:")
    : base.replace(/^http:/, "ws:");

  const qs = new URLSearchParams();
  const audience = inferPtAudience(opts?.pt_role, opts?.pt_code);
  qs.set("role", audience);
  qs.set("audience", audience);
  qs.set("portal_role", "pt");
  qs.set("pt_identity_key", makePtPresenceKey(opts?.org_code, opts?.pt_code, ptPk, opts?.pt_role));
  qs.set("pt_id", clean(ptPk));
  qs.set("pt_pk", clean(ptPk));

  if (clean(opts?.display_name))
    qs.set("display_name", clean(opts?.display_name));
  if (clean(opts?.org_code)) qs.set("org_code", clean(opts?.org_code));
  if (clean(opts?.pt_code)) qs.set("pt_code", clean(opts?.pt_code));
  if (clean(opts?.pt_role)) qs.set("pt_role", clean(opts?.pt_role));
  if (clean(opts?.avatar_id)) qs.set("avatar_id", clean(opts?.avatar_id));
  if (clean(opts?.avatar_src)) qs.set("avatar_src", clean(opts?.avatar_src));
  if (clean(opts?.token)) qs.set("token", normalizeBearer(clean(opts?.token)));

  return `${wsBase}/realtime/ws?${qs.toString()}`;
}

function getNumberFromAny(...values: any[]) {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function normalizeTipsPayload(data: any): TipLite[] {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.tips)
        ? data.tips
        : [];

  return raw
    .map((item: any, idx: number) => {
      const id = item?.id ?? `${clean(item?.category) || "tip"}_${idx}`;
      const body = clean(
        item?.body || item?.text || item?.content || item?.message,
      );
      const title = clean(item?.title || item?.name || item?.headline);
      if (!body && !title) return null;
      return {
        id,
        category: item?.category ?? null,
        title: title || "Wellness tip",
        body,
        status: item?.status ?? null,
        image_url: item?.image_url ?? item?.imageUrl ?? null,
        author_name: item?.author_name ?? item?.authorName ?? null,
        author_role: item?.author_role ?? item?.authorRole ?? null,
      } as TipLite;
    })
    .filter(Boolean) as TipLite[];
}

function persistToBoth(key: string, value: string) {
  const v = clean(value);
  if (!v) return;
  try {
    sessionStorage.setItem(key, v);
  } catch {}
  try {
    localStorage.setItem(key, v);
  } catch {}
}

function removeFromBoth(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {}
  try {
    localStorage.removeItem(key);
  } catch {}
}

function persistPortalAuthPatch(
  patch: Partial<PortalAuthState> & Record<string, any>,
) {
  const token = normalizeBearer(
    clean(
      patch.token ||
        patch.access_token ||
        patch.pt_access_token ||
        patch.authToken,
    ),
  );
  const ptCode = clean(
    patch.ptCode || patch.pt_code || patch.parent_teacher_id || patch.username,
  );
  const ptPk = clean(patch.ptPk || patch.pt_pk || patch.user_id || patch.id);
  const ptRoleRaw = clean(patch.ptRole || patch.pt_role || patch.role);
  const ptRole = ["parent", "teacher"].includes(ptRoleRaw.toLowerCase())
    ? ptRoleRaw.toLowerCase()
    : inferPtAudience(ptRoleRaw, clean(patch.ptCode || patch.pt_code || patch.parent_teacher_id || patch.username));
  const orgCodeRaw = clean(
    patch.orgCodeRaw ||
      patch.orgCode ||
      patch.org_code ||
      patch.organization_code ||
      patch.org,
  );
  const orgId = getNumberFromAny(
    patch.orgId,
    patch.org_id,
    patch.organization_id,
    patch.school_org_id,
  );
  const displayName = clean(
    patch.displayName ||
      patch.display_name ||
      patch.name ||
      patch.full_name ||
      patch.user_name,
  );
  const avatarId = clean(patch.avatarId || patch.avatar_id);
  const avatarSrc = clean(patch.avatarSrc || patch.avatar_src);

  if (token) {
    persistToBoth("pt_access_token", token);
    persistToBoth("pt_token", token);
    persistToBoth("access_token", token);
    persistToBoth("token", token);
  }
  if (ptCode) persistToBoth("pt_code", ptCode);
  if (ptPk) persistToBoth("pt_pk", ptPk);
  if (ptRole) persistToBoth("pt_role", ptRole);
  if (orgCodeRaw) persistToBoth("org_code", orgCodeRaw);
  if (orgId) {
    persistToBoth("org_id", String(orgId));
    persistToBoth("organization_id", String(orgId));
    persistToBoth("school_org_id", String(orgId));
  }
  if (displayName) {
    persistToBoth("display_name", displayName);
    persistToBoth("pt_display_name", displayName);
    persistToBoth("name", displayName);
  }
  if (avatarId) {
    persistToBoth("pt_avatar_id", avatarId);
    persistToBoth("avatar_id", avatarId);
  }
  if (avatarSrc) {
    persistToBoth("pt_avatar_src", avatarSrc);
    persistToBoth("avatar_src", avatarSrc);
  }
}

function readPortalAuth(): PortalAuthState {
  const token = normalizeBearer(
    getFirst([
      "pt_access_token",
      "pt_token",
      "access_token",
      "token",
      "authToken",
      "auth_token",
    ]),
  );

  const ptCode = getFirst([
    "pt_code",
    "parent_teacher_id",
    "parent_teacher_code",
    "pt_id",
    "username",
  ]);

  const ptPk = getFirst(["pt_pk", "pt_user_pk", "user_id", "id"]);

  const ptRoleRaw = getFirst(["pt_role", "role", "user_role"]);
  const ptRole = ["parent", "teacher"].includes(ptRoleRaw.toLowerCase())
    ? ptRoleRaw.toLowerCase()
    : inferPtAudience(ptRoleRaw, ptCode);

  const orgCodeRaw = getFirst([
    "org_code",
    "organization_code",
    "school_code",
    "org",
  ]);

  const orgId = Number(
    getFirst(["org_id", "organization_id", "school_org_id", "school_id"]) || 0,
  );

  const displayName = getFirst([
    "display_name",
    "pt_display_name",
    "name",
    "full_name",
  ]);

  const avatarId = getFirst(["pt_avatar_id", "avatar_id"]);
  const avatarSrc = getFirst(["pt_avatar_src", "avatar_src"]);

  return {
    token,
    ptCode,
    ptPk,
    ptRole,
    orgCodeRaw,
    orgCode: normOrgCode(orgCodeRaw),
    orgId,
    displayName,
    avatarId,
    avatarSrc,
  };
}

async function postAiChat(
  message: string,
  opts?: {
    ptRole?: string;
    ptCode?: string;
    ptPk?: string;
    orgCode?: string;
    orgId?: number;
    displayName?: string;
    sessionId?: string;
    conversationState?: Record<string, any>;
    token?: string;
  },
): Promise<AiChatResponse> {
  const msg = clean(message);

  const conversationState =
    opts?.conversationState && typeof opts.conversationState === "object"
      ? opts.conversationState
      : {};

  const speakerRole = inferPtAudience(opts?.ptRole, opts?.ptCode);

  const orgCode = clean(opts?.orgCode);
  const orgId = Number(opts?.orgId || 0) || 0;
  const ptCode = clean(opts?.ptCode);
  const ptPk = clean(opts?.ptPk);
  const ptRole = clean(opts?.ptRole) || speakerRole;
  const displayName = clean(opts?.displayName);
  const authToken = normalizeBearer(clean(opts?.token));

  const ptIdentityPayload = {
    audience: speakerRole,
    role: speakerRole,
    user_role: speakerRole,
    speaker_role: speakerRole,
    portal_role: "pt",
    requester_role: speakerRole,
    source: "parent_teacher_portal",
    client_source: "parent_teacher_portal",
    pt_role: ptRole,
    pt_audience: speakerRole,
    pt_code: ptCode,
    pt_pk: ptPk,
    pt_id: ptPk,
    org_code: orgCode,
    school_code: orgCode,
    organization_code: orgCode,
    org_id: orgId,
    organization_id: orgId,
    display_name: displayName,
    pt_identity_key: makePtPresenceKey(orgCode, ptCode, ptPk, ptRole),
  };

  const res = await api.post(
    "/ai/chat",
    {
      message: msg,
      session_id: clean(opts?.sessionId),
      conversation_state: conversationState,
      ...ptIdentityPayload,
      context: {
        ...ptIdentityPayload,
        conversation_state: conversationState,
        state: conversationState,
      },
      metadata: {
        ...ptIdentityPayload,
      },
    },
    authToken
      ? {
          headers: {
            Authorization: authToken,
            "X-Portal-Role": speakerRole,
            "X-Audience": speakerRole,
            "X-PT-Code": ptCode,
            "X-Org-Code": orgCode,
          },
        }
      : undefined,
  );

  const data = res?.data ?? {};

  return {
    reply: String(data?.reply ?? ""),
    trigger_level: String(data?.trigger_level ?? "none"),
    escalation: !!data?.escalation,
    pt_id: data?.pt_id,
    pt_role: data?.pt_role ?? clean(opts?.ptRole),
    session_id: String(data?.session_id ?? clean(opts?.sessionId)),
    analysis:
      typeof data?.analysis === "object" && data?.analysis ? data.analysis : {},
    classification:
      typeof data?.classification === "object" && data?.classification
        ? data.classification
        : {},
    metadata:
      typeof data?.metadata === "object" && data?.metadata ? data.metadata : {},
    state: typeof data?.state === "object" && data?.state ? data.state : {},
    conversation_state:
      typeof data?.state?.conversation_state === "object" &&
      data?.state?.conversation_state
        ? data.state.conversation_state
        : typeof data?.metadata?.conversation_state === "object" &&
            data?.metadata?.conversation_state
          ? data.metadata.conversation_state
          : typeof data?.conversation_state === "object" &&
              data?.conversation_state
            ? data.conversation_state
            : {},
    actions: Array.isArray(data?.actions) ? data.actions : [],
    safety_plan: Array.isArray(data?.safety_plan) ? data.safety_plan : [],
  };
}

function mapDoctorsFromDb(doctors: any[]): DoctorLite[] {
  const mapped = (Array.isArray(doctors) ? doctors : [])
    .map((d) => {
      const id = Number(d?.id ?? 0);
      if (!id) return null;

      const isActive = d?.is_active == null ? true : !["0", "false", "no", "inactive", "retired", "disabled"].includes(String(d?.is_active).trim().toLowerCase());
      if (!isActive) return null;

      const name = String(d?.name ?? "").trim() || `Advisor ${id}`;
      const photoUrlRaw = (d?.photo_url ?? null) as string | null;

      return {
        id,
        name,
        specialties: (d?.specialties ?? null) as string | null,
        medical_field: (d?.medical_field ?? null) as string | null,
        status: (d?.status ?? null) as string | null,
        photo_url: photoUrlRaw ? resolveUrl(String(photoUrlRaw)) : null,
        is_online: d?.is_online != null ? !!d?.is_online : false,
        is_available: d?.is_available != null ? !!d?.is_available : false,
        email: (d?.email ?? null) as string | null,
      } as DoctorLite;
    })
    .filter(Boolean) as DoctorLite[];

  mapped.sort((a, b) => {
    const on = Number(!!b.is_online) - Number(!!a.is_online);
    if (on) return on;
    const av = Number(!!b.is_available) - Number(!!a.is_available);
    if (av) return av;
    return (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    });
  });

  return mapped;
}

function mapDoctorsFromPresence(doctors: any[]): DoctorLite[] {
  const mapped = (Array.isArray(doctors) ? doctors : [])
    .map((d) => {
      const id = Number(d?.id ?? d?.doctor_id ?? d?.meta?.doctor_id ?? 0);
      if (!id) return null;

      const meta = d?.meta || {};
      const name =
        String(d?.name ?? meta?.name ?? meta?.display_name ?? "").trim() ||
        `Advisor ${id}`;
      const photoUrlRaw = (d?.photo_url ?? meta?.photo_url ?? null) as
        | string
        | null;

      return {
        id,
        name,
        specialties: (d?.specialties ?? meta?.specialties ?? null) as
          | string
          | null,
        medical_field: (d?.medical_field ?? meta?.medical_field ?? null) as
          | string
          | null,
        status: (d?.status ?? meta?.status ?? null) as string | null,
        photo_url: photoUrlRaw ? resolveUrl(String(photoUrlRaw)) : null,
        is_online: d?.is_online != null ? !!d?.is_online : true,
        is_available: d?.is_available != null ? !!d?.is_available : true,
        email: (d?.email ?? meta?.email ?? null) as string | null,
      } as DoctorLite;
    })
    .filter(Boolean) as DoctorLite[];

  mapped.sort((a, b) => {
    const on = Number(!!b.is_online) - Number(!!a.is_online);
    if (on) return on;
    const av = Number(!!b.is_available) - Number(!!a.is_available);
    if (av) return av;
    return (a.name || "").localeCompare(b.name || "", undefined, {
      sensitivity: "base",
    });
  });

  return mapped;
}

function mergeDoctorLists(
  roster: DoctorLite[],
  presence: DoctorLite[],
): DoctorLite[] {
  const byId = new Map<number, DoctorLite>();
  for (const d of roster || []) byId.set(Number(d.id), d);

  for (const p of presence || []) {
    const id = Number(p.id);
    if (!id) continue;
    const cur = byId.get(id);
    byId.set(id, {
      ...(cur || ({} as DoctorLite)),
      ...(p || ({} as DoctorLite)),
      id,
    });
  }

  const out = Array.from(byId.values());
  out.sort((a, b) => {
    const on = Number(!!b.is_online) - Number(!!a.is_online);
    if (on) return on;
    const av = Number(!!b.is_available) - Number(!!a.is_available);
    if (av) return av;
    return String(a.name || "").localeCompare(String(b.name || ""), undefined, {
      sensitivity: "base",
    });
  });

  return out;
}

function sameDoctorList(a: DoctorLite[], b: DoctorLite[]) {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];

    if (
      left?.id !== right?.id ||
      !!left?.is_online !== !!right?.is_online ||
      !!left?.is_available !== !!right?.is_available ||
      String(left?.status || "") !== String(right?.status || "") ||
      String(left?.photo_url || "") !== String(right?.photo_url || "") ||
      String(left?.name || "") !== String(right?.name || "") ||
      String(left?.medical_field || "") !==
        String(right?.medical_field || "") ||
      String(left?.specialties || "") !== String(right?.specialties || "")
    ) {
      return false;
    }
  }

  return true;
}

function AvatarCircle({
  src,
  fallback,
  alt,
}: {
  src?: string | null;
  fallback: string;
  alt: string;
}) {
  return (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        overflow: "hidden",
        background: "rgba(255,0,0,0.08)",
        border: "1px solid rgba(0,0,0,0.08)",
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
        fontWeight: 900,
      }}
      aria-label={alt}
      title={alt}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        fallback
      )}
    </div>
  );
}

function DoctorProfileModal({
  open,
  doctor,
  onClose,
}: {
  open: boolean;
  doctor: DoctorLite | null;
  onClose: () => void;
}) {
  if (!open || !doctor) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,0.36)",
        display: "grid",
        placeItems: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(92vw, 560px)",
          background: "#fff",
          borderRadius: 20,
          padding: 20,
          boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <AvatarCircle
            src={doctor.photo_url || ""}
            fallback="D"
            alt={doctor.name || "Advisor"}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.15 }}>
              {doctor.name}
            </div>
            <div style={{ marginTop: 4, opacity: 0.8 }}>
              {doctor.medical_field || doctor.specialties || "General Medicine"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "1px solid rgba(0,0,0,0.15)",
              background: "#fff",
              borderRadius: 10,
              padding: "6px 10px",
              cursor: "pointer",
              fontWeight: 900,
              height: 36,
            }}
          >
            ✕
          </button>
        </div>

        {doctor.email ? (
          <div style={{ marginTop: 12, opacity: 0.82 }}>
            <span style={{ fontWeight: 900 }}>Email:</span> {doctor.email}
          </div>
        ) : null}
      </div>
    </div>
  );
}

type ParentTeacherPortalProps = { forcedAudience?: "parent" | "teacher" };

export default function ParentTeacherPortal({ forcedAudience }: ParentTeacherPortalProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const year = new Date().getFullYear();

  const VideoRoomAny = VideoRoom as any;

  const [auth, setAuth] = useState<PortalAuthState>(() => readPortalAuth());
  const authRef = useRef<PortalAuthState>(auth);

  useEffect(() => {
    authRef.current = auth;
  }, [auth]);

  const refreshAuth = useCallback(() => {
    const next = readPortalAuth();
    authRef.current = next;
    setAuth(next);
    return next;
  }, []);

  useEffect(() => {
    const here = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    setPortalReturn(here || PT_PORTAL_ROUTE);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const state = (location.state as Record<string, any> | null) || null;
    const query = new URLSearchParams(location.search || "");

    const queryPatch: Record<string, any> = {
      token:
        query.get("token") ||
        query.get("access_token") ||
        query.get("pt_access_token") ||
        "",
      pt_code: query.get("pt_code") || query.get("code") || "",
      pt_pk: query.get("pt_pk") || query.get("id") || "",
      pt_role: query.get("pt_role") || query.get("role") || "",
      org_code: query.get("org_code") || query.get("organization_code") || "",
      org_id: query.get("org_id") || query.get("organization_id") || "",
      display_name: query.get("display_name") || query.get("name") || "",
      avatar_id: query.get("avatar_id") || "",
      avatar_src: query.get("avatar_src") || "",
    };

    if (state || Object.values(queryPatch).some((v) => clean(v))) {
      persistPortalAuthPatch({
        ...(state || {}),
        ...queryPatch,
      });
      refreshAuth();
    }
  }, [location.search, location.state, refreshAuth]);

  const ptCode = auth.ptCode;
  const ptPk = auth.ptPk;
  const ptRole = auth.ptRole;
  const orgCodeRaw = auth.orgCodeRaw;
  const orgCode = auth.orgCode;

  const ptAudience = useMemo(() => forcedAudience || inferPtAudience(ptRole, ptCode), [forcedAudience, ptRole, ptCode]);

  const videoRole = useMemo(() => ptAudience, [ptAudience]) as VideoRoomRole;

  const caseySessionStorageKey = useMemo(
    () =>
      `casey_session:${ptAudience}:${orgCode}:${clean(ptPk) || clean(ptCode) || "anon"}`,
    [ptAudience, orgCode, ptPk, ptCode],
  );

  const [caseySessionId, setCaseySessionId] = useState(() =>
    getStoredCaseySessionId(
      `casey_session:${inferPtAudience(ptRole, ptCode)}:${normOrgCode(orgCodeRaw)}:${clean(ptPk) || clean(ptCode) || "anon"}`,
      makeCaseySessionId(
        inferPtAudience(ptRole, ptCode),
        clean(ptPk) || clean(ptCode) || "anon",
        normOrgCode(orgCodeRaw),
      ),
    ),
  );

  useEffect(() => {
    const fallback = makeCaseySessionId(
      ptAudience,
      clean(ptPk) || clean(ptCode) || "anon",
      orgCode,
    );
    const stable = getStoredCaseySessionId(caseySessionStorageKey, fallback);
    setCaseySessionId(stable);
  }, [caseySessionStorageKey, orgCode, ptPk, ptCode, ptAudience]);

  const fallbackAvatars: AvatarItem[] = useMemo(() => {
    const publishedNames = ["02", "04", "05", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17"];
    return publishedNames.map((n) => ({
      id: `culture_${n}.png`,
      label: `Avatar ${n}`,
      src: `${API_BASE}/uploads/avatars_public/culture_${n}.png`,
      tags: ["pt"],
    }));
  }, []);

  const [remoteAvatars, setRemoteAvatars] = useState<AvatarItem[]>([]);
  const AVATARS: AvatarItem[] = remoteAvatars.length ? remoteAvatars : fallbackAvatars;

  useEffect(() => {
    let cancelled = false;

    const loadPublishedAvatars = async () => {
      try {
        const response = await fetch(`${API_BASE}/public/avatars?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!response.ok) throw new Error(`Avatar list failed (${response.status})`);

        const payload = await response.json().catch(() => ({}));
        const rawItems = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload?.avatars)
              ? payload.avatars
              : [];

        const normalized = rawItems
          .map((raw: any, index: number) => {
            const filename = clean(raw?.file_name || raw?.filename || raw?.name || raw?.id);
            const id = clean(raw?.id || filename || `avatar_${index + 1}`);
            const label = clean(raw?.label || raw?.display_name || raw?.title || filename || id);
            const candidate = clean(raw?.src || raw?.url || raw?.image_url || raw?.public_url || raw?.path || filename);
            if (!candidate) return null;

            const src = candidate.startsWith("http://") || candidate.startsWith("https://")
              ? candidate
              : candidate.startsWith("/")
                ? `${API_BASE}${candidate}`
                : `${API_BASE}/uploads/avatars_public/${encodeURIComponent(candidate)}`;

            return { id, label, src, tags: ["pt"] } as AvatarItem;
          })
          .filter(Boolean) as AvatarItem[];

        if (!cancelled && normalized.length) setRemoteAvatars(normalized);
      } catch (error) {
        console.warn("Published avatar feed unavailable; using production fallback avatars.", error);
      }
    };

    void loadPublishedAvatars();
    return () => {
      cancelled = true;
    };
  }, []);

  const normalizeSelectedAvatarSrc = useCallback((rawSrc?: string, rawId?: string) => {
    const src = clean(rawSrc);
    const id = clean(rawId);
    const legacyMatch = `${src} ${id}`.match(/avatar[_/-]?(\d{1,2})/i);
    if (legacyMatch) {
      const n = String(Number(legacyMatch[1])).padStart(2, "0");
      return `${API_BASE}/uploads/avatars_public/culture_${n}.png`;
    }
    return src ? resolveUrl(src) : "";
  }, []);

  const [displayName, setDisplayName] = useState(auth.displayName || "");
  const [avatarId, setAvatarId] = useState<string>(auth.avatarId || "");
  const [avatarSrc, setAvatarSrc] = useState<string>(() => normalizeSelectedAvatarSrc(auth.avatarSrc, auth.avatarId));
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    setDisplayName((prev) => prev || auth.displayName || "");
  }, [auth.displayName]);

  useEffect(() => {
    setAvatarId((prev) => prev || auth.avatarId || "");
  }, [auth.avatarId]);

  useEffect(() => {
    setAvatarSrc((prev) => prev || normalizeSelectedAvatarSrc(auth.avatarSrc, auth.avatarId));
  }, [auth.avatarId, auth.avatarSrc, normalizeSelectedAvatarSrc]);

  const [doctorProfileOpen, setDoctorProfileOpen] = useState(false);
  const [doctorProfileDoc, setDoctorProfileDoc] = useState<DoctorLite | null>(
    null,
  );

  const [tab, setTab] = useState<"ai" | "live">("ai");

  const [aiText, setAiText] = useState("");
  const [aiMessages, setAiMessages] = useState<
    Array<{ id: string; from: "pt" | "system"; text: string; ts: number }>
  >([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [translateLanguage, setTranslateLanguage] = useState("English");
  const translateLanguageRef = useRef("English");
  const [systemNotice, setSystemNotice] = useState<string>("");
  const [tipCategory, setTipCategory] = useState<TipCategory>("mental");
  const [tips, setTips] = useState<TipLite[]>([]);
  const [tipsBusy, setTipsBusy] = useState(false);
  const [tipsError, setTipsError] = useState("");
  const [expandedTipId, setExpandedTipId] = useState<string>("");
  const [aiConversationState, setAiConversationState] = useState<
    Record<string, any>
  >({});

  const ptRoleLabel = forcedAudience === "teacher" ? "Teacher" : forcedAudience === "parent" ? "Parent" : displayPtRole(ptRole, ptCode);
  const portalTitle = ptAudience === "teacher" ? "Teacher Portal" : "Parent Portal";
  const portalSubtitle = ptAudience === "teacher"
    ? "Classroom wellness support, teacher resources, live advisor access, and Casey AI guidance."
    : "Family wellness support, live advisor access, Casey Academy, and Casey AI guidance.";
  const welcomeCopy = ptAudience === "teacher"
    ? "Casey is here to help you support students, strengthen classroom wellness, and respond to emerging concerns."
    : "Casey is here to help you support your child and the young people who depend on you.";
  const greetingName = clean(displayName) || clean(ptCode) || ptRoleLabel;
  const caseyGreeting = `Hi ${greetingName}, I’m Casey. How can I help you today?`;
  const ptFallback = (clean(displayName) || clean(ptCode) || "P")
    .slice(0, 1)
    .toUpperCase();

  const activatePtAuraSos = useCallback(
    async (draft: AuraSosActivationDraft) => {
      const currentAuth = authRef.current;

      if (!currentAuth.token || !currentAuth.ptPk) {
        throw new Error(
          "Your Parent/Teacher session has expired. Please sign in again.",
        );
      }

      const sosDescriptionOriginal = clean(draft.description);
      const sosDescriptionEnglish =
        sosDescriptionOriginal && translateLanguage !== "English"
          ? await translateText(sosDescriptionOriginal, "English")
          : sosDescriptionOriginal;

      const response = await fetch(`${API_BASE}/aura/sos/pt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${normalizeBearer(currentAuth.token)}`,
        },
        body: JSON.stringify({
          pt_pk: currentAuth.ptPk,
          pt_code: currentAuth.ptCode,
          pt_role: ptAudience,
          org_code: currentAuth.orgCodeRaw || currentAuth.orgCode,
          org_id: currentAuth.orgId || undefined,
          display_name:
            clean(displayName) ||
            clean(currentAuth.displayName) ||
            greetingName,
          category: draft.category,
          description: sosDescriptionOriginal,
          description_english: sosDescriptionEnglish,
          preferred_language: translateLanguage,
          created_at: draft.created_at,
          location: draft.location || null,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.ok === false) {
        throw new Error(
          data?.detail ||
            data?.message ||
            "AURA SOS could not be activated.",
        );
      }

      const incident = data?.incident || {};
      const incidentId = clean(incident?.incident_id);
      const canonicalRoom = clean(
        incident?.student_chat_room_id ||
          incident?.chat_room_id ||
          (incidentId ? `erh:${incidentId}` : ""),
      );

      try {
        sessionStorage.setItem(
          "aura_sos_active_incident",
          JSON.stringify(incident),
        );
      } catch {}

      // Parent/Teacher ERH uses the same canonical intervention room as the
      // attending advisor. We do not create a second chat system.
      if (canonicalRoom) {
        setRoomId(canonicalRoom);
        roomIdRef.current = canonicalRoom;
      }

      setInviteMode("chat");
      inviteModeRef.current = "chat";
      setShowVideoRoom(false);
      setTab("live");

      setLiveMsgs((prev) => [
        ...prev,
        {
          id: makeClientMsgId(),
          from: "system",
          text: incidentId
            ? `AURA ERH™ incident ${incidentId} is active. An available advisor has been alerted. You can use this private chat while the response is coordinated.`
            : "AURA ERH™ is active. An available advisor has been alerted. You can use this private chat while the response is coordinated.",
          ts: Date.now(),
        },
      ]);

      const initialDescription = clean(sosDescriptionEnglish || draft.description);
      if (canonicalRoom && initialDescription) {
        const clientMsgId = makeClientMsgId();
        pendingErhIntroRef.current = {
          roomId: canonicalRoom,
          text: initialDescription,
          clientMsgId,
        };

        // Show the reporter exactly what was entered in the SOS information box.
        setLiveMsgs((prev) => [
          ...prev,
          {
            id: clientMsgId,
            from: "pt",
            text: initialDescription,
            ts: Date.now(),
          },
        ]);
      }

      // If the realtime socket is already open, join immediately. Otherwise
      // ws.onopen below will join roomIdRef.current after the Live tab opens.
      try {
        if (
          canonicalRoom &&
          wsRef.current &&
          wsRef.current.readyState === WebSocket.OPEN
        ) {
          wsRef.current.send(
            JSON.stringify({ type: "chat.join", room_id: canonicalRoom }),
          );

          const pendingIntro = pendingErhIntroRef.current;
          if (pendingIntro && pendingIntro.roomId === canonicalRoom) {
            const introWireText =
              translateLanguageRef.current !== "English"
                ? await translateText(pendingIntro.text, "English")
                : pendingIntro.text;

            wsRef.current.send(
              JSON.stringify({
                type: "chat.message",
                room_id: canonicalRoom,
                text: introWireText || pendingIntro.text,
                client_msg_id: pendingIntro.clientMsgId,
                from: "pt",
                from_role: ptAudience,
                pt_id: clean(currentAuth.ptPk),
                pt_pk: clean(currentAuth.ptPk),
                pt_code: clean(currentAuth.ptCode),
                pt_role: clean(currentAuth.ptRole) || ptAudience,
                org_code: currentAuth.orgCode,
                display_name:
                  clean(displayName) ||
                  clean(currentAuth.displayName) ||
                  greetingName,
              }),
            );
            pendingErhIntroRef.current = null;
          }
        }
      } catch {}

      setSystemNotice(
        `AURA SOS is active${incidentId ? ` • Incident ${incidentId}` : ""}. An available advisor has been alerted.`,
      );
    },
    [displayName, greetingName, ptAudience],
  );

  const aiScrollBoxRef = useRef<HTMLDivElement | null>(null);
  const aiEndRef = useRef<HTMLDivElement | null>(null);
  const liveScrollBoxRef = useRef<HTMLDivElement | null>(null);
  const liveEndRef = useRef<HTMLDivElement | null>(null);

  const [onlineDoctors, setOnlineDoctors] = useState<DoctorLite[]>([]);
  const [doctorSource, setDoctorSource] = useState("Loading advisors...");
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>("");
  const selectedDoctor = useMemo(
    () =>
      onlineDoctors.find((d) => String(d.id) === String(selectedDoctorId)) ||
      null,
    [onlineDoctors, selectedDoctorId],
  );

  const [roomId, setRoomId] = useState("");
  const activeErhRoom = clean(roomId).toLowerCase().startsWith("erh:");
  const effectiveTab: "ai" | "live" = activeErhRoom ? "live" : tab;
  const roomIdRef = useRef("");
  const pendingErhIntroRef = useRef<{ roomId: string; text: string; clientMsgId: string } | null>(null);
  const [liveText, setLiveText] = useState("");
  const [liveMsgs, setLiveMsgs] = useState<LiveMsg[]>([]);
  const [inviteMode, setInviteMode] = useState<InviteMode>("chat");
  const inviteModeRef = useRef<InviteMode>("chat");
  const [showVideoRoom, setShowVideoRoom] = useState(false);
  const [wsStatus, setWsStatus] = useState<
    "idle" | "connecting" | "connected" | "disconnected"
  >("idle");
  const [wsError, setWsError] = useState("");
  const [sessionWarning, setSessionWarning] = useState("");

  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  useEffect(() => {
    translateLanguageRef.current = clean(translateLanguage) || "English";
  }, [translateLanguage]);

  useEffect(() => {
    inviteModeRef.current = inviteMode;
  }, [inviteMode]);

  const liveStatusLabel =
    wsStatus === "connected"
      ? "Connected"
      : wsStatus === "connecting"
        ? "Connecting..."
        : wsStatus === "disconnected"
          ? "Disconnected"
          : "";

  useEffect(() => {
    const current = refreshAuth();
    if (!current.ptPk || !current.token) {
      setSessionWarning(
        "Your Parent/Teacher session looks incomplete. Please sign in again.",
      );
    } else {
      setSessionWarning("");
    }
  }, [refreshAuth]);

  const scrollToBottom = useCallback(
    (box: HTMLDivElement | null, end: HTMLDivElement | null, smooth = true) => {
      try {
        if (end?.scrollIntoView) {
          end.scrollIntoView({
            behavior: smooth ? "smooth" : "auto",
            block: "end",
          });
        }
        if (box) box.scrollTop = box.scrollHeight;
      } catch {}
    },
    [],
  );

  useEffect(() => {
    if (tab !== "ai") return;
    const raf = window.requestAnimationFrame(() => {
      scrollToBottom(aiScrollBoxRef.current, aiEndRef.current, true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [tab, aiMessages.length, scrollToBottom]);

  useEffect(() => {
    if (tab !== "live") return;
    const raf = window.requestAnimationFrame(() => {
      scrollToBottom(liveScrollBoxRef.current, liveEndRef.current, true);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [tab, liveMsgs.length, scrollToBottom]);

  useEffect(() => {
    if (!greetingName) return;
    const key = `casey_spoken_pt:${orgCode}:${clean(ptCode)}:${clean(ptPk)}`;
    try {
      if (sessionStorage.getItem(key) === "1") return;
      sessionStorage.setItem(key, "1");
    } catch {}
    const t = window.setTimeout(() => {
      speakCasey(caseyGreeting);
    }, 500);
    return () => window.clearTimeout(t);
  }, [caseyGreeting, greetingName, orgCode, ptCode, ptPk]);

  useEffect(() => {
    try {
      localStorage.setItem("display_name", clean(displayName));
      sessionStorage.setItem("display_name", clean(displayName));
      localStorage.setItem("pt_avatar_id", clean(avatarId));
      sessionStorage.setItem("pt_avatar_id", clean(avatarId));
      localStorage.setItem("pt_avatar_src", clean(avatarSrc));
      sessionStorage.setItem("pt_avatar_src", clean(avatarSrc));
      localStorage.setItem("avatar_id", clean(avatarId));
      sessionStorage.setItem("avatar_id", clean(avatarId));
      localStorage.setItem("avatar_src", clean(avatarSrc));
      sessionStorage.setItem("avatar_src", clean(avatarSrc));
    } catch {}
  }, [displayName, avatarId, avatarSrc]);


  const translateText = async (text: string, target: string): Promise<string> => {
    if (!text) return text;
    try {
      const res = await api.post("/translate", {
        text,
        target: getLanguageCode(target),
        source: "auto",
      });
      return res?.data?.translated_text || res?.data?.translation || res?.data?.text || text;
    } catch {
      return text;
    }
  };

const sendAi = async () => {
    const text = clean(aiText);
    if (!text || aiBusy) return;
    let outgoingText = text;
    if (translateLanguage !== "English") {
      outgoingText = await translateText(text, "English");
    }

    const currentAuth = refreshAuth();
    if (!currentAuth.ptPk || !currentAuth.token) {
      setSystemNotice(
        "Your session is missing login data. Please sign in again.",
      );
      return;
    }

    const nextPtMessage = {
      id: makeClientMsgId(),
      from: "pt" as const,
      text,
      ts: Date.now(),
    };

    setAiMessages((prev) => [...prev, nextPtMessage]);
    setAiText("");
    setAiBusy(true);
    setSystemNotice("");

    try {
      const data = await postAiChat(outgoingText, {
        ptRole: currentAuth.ptRole,
        ptCode: currentAuth.ptCode,
        ptPk: currentAuth.ptPk,
        orgCode: currentAuth.orgCode,
        orgId: currentAuth.orgId,
        displayName:
          clean(displayName) || clean(currentAuth.displayName) || greetingName,
        sessionId: caseySessionId,
        conversationState: aiConversationState,
        token: currentAuth.token,
      });

      try {
        console.info("[PT_CASEY_DEBUG]", {
          request: {
            ptRole: currentAuth.ptRole,
            audience: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
            sessionId: caseySessionId,
          },
          classification: data.classification,
          metadata: data.metadata,
          state: data.state,
        });
      } catch {}

      const returnedSessionId = clean(data.session_id);
      if (returnedSessionId && returnedSessionId !== caseySessionId) {
        setCaseySessionId(returnedSessionId);
        persistCaseySessionId(caseySessionStorageKey, returnedSessionId);
      } else if (caseySessionId) {
        persistCaseySessionId(caseySessionStorageKey, caseySessionId);
      }

      const nextConversationState =
        data.conversation_state && typeof data.conversation_state === "object"
          ? data.conversation_state
          : data.state?.conversation_state &&
              typeof data.state.conversation_state === "object"
            ? data.state.conversation_state
            : data.metadata?.conversation_state &&
                typeof data.metadata.conversation_state === "object"
              ? data.metadata.conversation_state
              : {};

      if (Object.keys(nextConversationState).length > 0) {
        setAiConversationState(nextConversationState);
      }

      const baseReply =
        clean(data.reply) ||
        "Casey is here with you. Can you tell me a little more?";

      const lastSystem = [...aiMessages]
        .reverse()
        .find((m) => m.from === "system");
      const isRepeat = !!(
        lastSystem && clean(lastSystem.text) === clean(baseReply)
      );

      let translatedReply = baseReply;
      if (translateLanguage !== "English") {
        translatedReply = await translateText(baseReply, translateLanguage);
      }

      const finalReply = isRepeat
        ? translateLanguage !== "English"
          ? `${translatedReply}`
          : `${baseReply} Tell me one detail: what feels hardest right now?`
        : translatedReply;

      const trigger = String(data.trigger_level ?? "none").toLowerCase();
      const escalation = !!data.escalation;

      await typeCaseyMessage(finalReply, setAiMessages, trigger);

      const urgentTriggers = [
        "immediate_danger",
        "high_risk",
        "critical",
        "high",
        "self_harm",
        "violence",
        "sexual_abuse",
        "physical_safety",
        "medical_urgent",
        "basic_needs",
        "unsafe_home",
        "abuse_physical",
      ];

      if (escalation || urgentTriggers.includes(trigger)) {
        if (
          trigger === "high_risk" ||
          trigger === "immediate_danger" ||
          trigger === "critical" ||
          trigger === "high"
        ) {
          setSystemNotice(
            "If you feel unsafe or in immediate danger, call or text 988 (U.S.) or contact a trusted person right now.",
          );
        } else {
          setSystemNotice(
            "If you are in immediate danger, call 911 (U.S.) now.",
          );
        }
      } else {
        setSystemNotice("");
      }

      window.requestAnimationFrame(() =>
        scrollToBottom(aiScrollBoxRef.current, aiEndRef.current, true),
      );
    } catch (err: any) {
      console.error(
        "AI chat error:",
        err?.response?.status,
        err?.response?.data || err,
      );
      setAiMessages((prev) => [
        ...prev,
        {
          id: makeClientMsgId(),
          from: "system",
          text: "AI is unavailable right now. Please try again in a moment.",
          ts: Date.now(),
        },
      ]);
    } finally {
      setAiBusy(false);
    }
  };

  const rosterRef = useRef<DoctorLite[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const wsRunRef = useRef(false);
  const wsAttemptRef = useRef(0);
  const wsConnectSeqRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const presencePollRef = useRef<number | null>(null);

  const subtleBtn: React.CSSProperties = {
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.88)",
    borderRadius: 12,
    padding: "9px 12px",
    cursor: "pointer",
    fontWeight: 900,
  };

  const clearCaseyChat = useCallback(() => {
    if (aiBusy) return;

    const freshSession = `${makeCaseySessionId(
      "pt",
      clean(ptPk) || clean(ptCode) || "anon",
      orgCode,
    )}:fresh:${Date.now()}`;

    try {
      window.speechSynthesis?.cancel();
    } catch {}

    setAiMessages([]);
    setAiText("");
    setAiConversationState({});
    setSystemNotice("");
    setCaseySessionId(freshSession);
    persistCaseySessionId(caseySessionStorageKey, freshSession);
  }, [aiBusy, caseySessionStorageKey, orgCode, ptCode, ptPk]);

  const stopReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const stopPing = useCallback(() => {
    if (pingTimerRef.current) {
      window.clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  const startPing = useCallback(() => {
    stopPing();
    pingTimerRef.current = window.setInterval(() => {
      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        }
      } catch {}
    }, 20000);
  }, [stopPing]);

  const stopPresencePoll = useCallback(() => {
    if (presencePollRef.current) {
      window.clearInterval(presencePollRef.current);
      presencePollRef.current = null;
    }
  }, []);

  const fetchJson = useCallback(async (url: string) => {
    const currentAuth = readPortalAuth();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (currentAuth.token)
      headers.Authorization = `Bearer ${currentAuth.token}`;
    const res = await fetch(url, { headers, credentials: "include" });
    const text = await res.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = text;
    }
    return { ok: res.ok, status: res.status, data };
  }, []);

  const refreshTips = useCallback(
    async (category: TipCategory) => {
      setTipsBusy(true);
      setTipsError("");
      try {
        const { ok, status, data } = await fetchJson(
          `${API_BASE}/tips/public?category=${encodeURIComponent(category)}`,
        );
        if (!ok) {
          setTips([]);
          setTipsError(`Tips unavailable (${status})`);
          return;
        }
        const nextTips = normalizeTipsPayload(data);
        setTips(nextTips);
        setExpandedTipId((prev) =>
          prev && nextTips.some((tip) => String(tip.id) === prev) ? prev : "",
        );
      } catch {
        setTips([]);
        setTipsError("Tips unavailable");
      } finally {
        setTipsBusy(false);
      }
    },
    [fetchJson],
  );

  useEffect(() => {
    void refreshTips(tipCategory);
  }, [refreshTips, tipCategory]);

  const refreshRosterFromDb = useCallback(async () => {
    try {
      let result = await fetchJson(`${API_BASE}/public/doctors`);
      if (!result.ok && (result.status === 401 || result.status === 403 || result.status === 404)) {
        result = await fetchJson(`${API_BASE}/public/advisors`);
      }
      const { ok, status, data } = result;
      if (!ok) {
        setDoctorSource(`Advisor roster failed (${status})`);
        return;
      }
      const roster = mapDoctorsFromDb(data?.doctors ?? data?.items ?? data?.advisors ?? []);
      rosterRef.current = roster;

      setOnlineDoctors((prev) => mergeDoctorLists(roster, prev));
      setDoctorSource("Advisor roster ✅");
      if (!clean(selectedDoctorId) && roster.length) {
        setSelectedDoctorId(String(roster[0].id));
      }
    } catch {
      setDoctorSource("Advisor roster error");
    }
  }, [fetchJson, selectedDoctorId]);

  const refreshDoctorsFromPresence = useCallback(async () => {
    try {
      if (!rosterRef.current.length) {
        await refreshRosterFromDb();
      }

      const { ok, status, data } = await fetchJson(
        `${API_BASE}/realtime/presence`,
      );
      if (!ok) {
        if (rosterRef.current.length) {
          setOnlineDoctors((prev) => mergeDoctorLists(rosterRef.current, prev));
          setDoctorSource(`Advisor roster ✅ (presence failed ${status})`);
        } else {
          setDoctorSource(`Advisor presence failed (${status})`);
        }
        return;
      }

      const doctorsRaw = data?.online?.doctors ?? data?.doctors ?? [];
      const presence = mapDoctorsFromPresence(doctorsRaw);
      const merged = mergeDoctorLists(rosterRef.current, presence);

      setOnlineDoctors((prev) =>
        sameDoctorList(prev, merged) ? prev : merged,
      );
      setDoctorSource("Advisor roster + presence ✅");
      if (!clean(selectedDoctorId) && merged.length) {
        setSelectedDoctorId(String(merged[0].id));
      }
    } catch {
      if (rosterRef.current.length) {
        setOnlineDoctors((prev) => mergeDoctorLists(rosterRef.current, prev));
        setDoctorSource("Advisor roster ✅ (presence error)");
      } else {
        setDoctorSource("Advisor presence error");
      }
    }
  }, [fetchJson, refreshRosterFromDb, selectedDoctorId]);

  useEffect(() => {
    void refreshRosterFromDb();
    void refreshDoctorsFromPresence();

    stopPresencePoll();
    presencePollRef.current = window.setInterval(() => {
      void refreshDoctorsFromPresence();
    }, 20000);

    return () => {
      stopPresencePoll();
    };
  }, [refreshDoctorsFromPresence, refreshRosterFromDb, stopPresencePoll]);

  const connectWsRef = useRef<() => Promise<void>>(async () => {});

  const closeWs = useCallback(() => {
    stopReconnectTimer();
    stopPing();
    try {
      wsRef.current?.close();
    } catch {}
    wsRef.current = null;
    setWsStatus("disconnected");
  }, [stopPing, stopReconnectTimer]);

  const scheduleReconnect = useCallback(() => {
    stopReconnectTimer();
    if (!wsRunRef.current) return;
    const delay = Math.min(1000 * Math.pow(2, wsAttemptRef.current), 10000);
    reconnectTimerRef.current = window.setTimeout(() => {
      void connectWsRef.current();
    }, delay);
  }, [stopReconnectTimer]);

  const connectWs = useCallback(async () => {
    const currentAuth = refreshAuth();
    if (!clean(currentAuth.ptPk)) return;

    stopReconnectTimer();

    const seq = ++wsConnectSeqRef.current;
    setWsStatus("connecting");
    setWsError("");

    try {
      if (!rosterRef.current.length) {
        await refreshRosterFromDb();
      }

      const wsUrl = buildPtWsUrl(API_BASE, currentAuth.ptPk, {
        display_name:
          clean(displayName) || clean(currentAuth.displayName) || greetingName,
        org_code: currentAuth.orgCode,
        pt_code: currentAuth.ptCode,
        pt_role: currentAuth.ptRole,
        avatar_id: avatarId,
        avatar_src: avatarSrc,
        token: currentAuth.token,
      });

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (seq !== wsConnectSeqRef.current) return;
        wsAttemptRef.current = 0;
        setWsStatus("connected");
        startPing();
        try {
          ws.send(
            JSON.stringify({
              type: "presence.update",
              role: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
              audience: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
              portal_role: "pt",
              pt_identity_key: makePtPresenceKey(currentAuth.orgCode, currentAuth.ptCode, currentAuth.ptPk, currentAuth.ptRole),
              pt_id: clean(currentAuth.ptPk),
              pt_pk: clean(currentAuth.ptPk),
              pt_code: clean(currentAuth.ptCode),
              pt_role: clean(currentAuth.ptRole),
              display_name:
                clean(displayName) ||
                clean(currentAuth.displayName) ||
                greetingName,
              org_code: currentAuth.orgCode,
              avatar_id: avatarId,
              avatar_src: avatarSrc,
              token: normalizeBearer(currentAuth.token),
            }),
          );
          ws.send(JSON.stringify({ type: "presence.get" }));

          const activeRoom = clean(roomIdRef.current);
          if (activeRoom) {
            ws.send(
              JSON.stringify({
                type: "chat.join",
                room_id: activeRoom,
              }),
            );

            const pendingIntro = pendingErhIntroRef.current;
            if (pendingIntro && pendingIntro.roomId === activeRoom) {
              void (async () => {
                const introWireText =
                  translateLanguageRef.current !== "English"
                    ? await translateText(pendingIntro.text, "English")
                    : pendingIntro.text;

                if (ws.readyState === WebSocket.OPEN) {
                  ws.send(
                    JSON.stringify({
                      type: "chat.message",
                      room_id: activeRoom,
                      text: introWireText || pendingIntro.text,
                      client_msg_id: pendingIntro.clientMsgId,
                      from: "pt",
                      from_role: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
                      pt_id: clean(currentAuth.ptPk),
                      pt_pk: clean(currentAuth.ptPk),
                      pt_code: clean(currentAuth.ptCode),
                      pt_role:
                        clean(currentAuth.ptRole) ||
                        inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
                      org_code: currentAuth.orgCode,
                      display_name:
                        clean(displayName) ||
                        clean(currentAuth.displayName) ||
                        greetingName,
                    }),
                  );
                  pendingErhIntroRef.current = null;
                }
              })();
            }
          }
        } catch {}
      };

      ws.onmessage = (evt) => {
        let raw: any = null;
        try {
          raw = JSON.parse(String(evt.data || "{}"));
        } catch {
          raw = { text: String(evt.data || "") };
        }

        const payload = raw?.payload ?? raw?.data ?? raw;

        if (isPresenceSnapshot(raw)) {
          const doctorsRaw = raw?.online?.doctors ?? raw?.doctors ?? [];
          const presence = mapDoctorsFromPresence(doctorsRaw);
          const merged = mergeDoctorLists(rosterRef.current, presence);
          setOnlineDoctors((prev) =>
            sameDoctorList(prev, merged) ? prev : merged,
          );
          setDoctorSource("Advisor presence ✅");
          return;
        }

        if (isChatMessage(raw)) {
          const text = clean(
            payload?.text ?? payload?.message ?? raw?.text ?? raw?.message,
          );
          if (!text) return;

          const incomingRoom = clean(payload?.room_id ?? raw?.room_id);
          const activeRoom = clean(roomIdRef.current);
          if (activeRoom && incomingRoom && incomingRoom !== activeRoom) return;

          const fromRaw = clean(
            payload?.from ??
              payload?.from_role ??
              payload?.sender ??
              payload?.sender_role ??
              raw?.from ??
              raw?.from_role ??
              raw?.sender ??
              raw?.sender_role,
          ).toLowerCase();

          const fromDoctor = !!(
            fromRaw.includes("doctor") || fromRaw.includes("advisor")
          );

          const incomingId =
            clean(
              payload?.client_msg_id ||
                payload?.server_msg_id ||
                raw?.client_msg_id,
            ) || makeClientMsgId();
          const incomingTs =
            typeof payload?.ts === "number" ? payload.ts : Date.now();

          if (
            fromDoctor &&
            clean(translateLanguageRef.current) &&
            translateLanguageRef.current !== "English"
          ) {
            void translateText(text, translateLanguageRef.current).then(
              (translatedText) => {
                setLiveMsgs((prev) => [
                  ...prev,
                  {
                    id: incomingId,
                    from: "doctor",
                    text: translatedText || text,
                    ts: incomingTs,
                  },
                ]);
              },
            );
          } else {
            setLiveMsgs((prev) => [
              ...prev,
              {
                id: incomingId,
                from: fromDoctor ? "doctor" : "system",
                text,
                ts: incomingTs,
              },
            ]);
          }

          window.requestAnimationFrame(() =>
            scrollToBottom(liveScrollBoxRef.current, liveEndRef.current, true),
          );
          return;
        }

        if (isInviteAccept(raw)) {
          const currentPtPk = clean(readPortalAuth().ptPk);
          const incomingRoomId =
            clean(payload?.room_id) ||
            clean(raw?.room_id) ||
            clean(raw?.roomId) ||
            makeRoomIdFromPk(currentPtPk, selectedDoctor?.id || 0);

          setRoomId(incomingRoomId);
          roomIdRef.current = incomingRoomId;

          try {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(
                JSON.stringify({ type: "chat.join", room_id: incomingRoomId }),
              );
            }
          } catch {}

          if (inviteModeRef.current === "video") setShowVideoRoom(true);

          setLiveMsgs((prev) => [
            ...prev,
            {
              id: makeClientMsgId(),
              from: "system",
              text: "Your advisor accepted the request.",
              ts: Date.now(),
            },
          ]);

          window.requestAnimationFrame(() =>
            scrollToBottom(liveScrollBoxRef.current, liveEndRef.current, true),
          );
          return;
        }

        if (isInviteDecline(raw)) {
          setLiveMsgs((prev) => [
            ...prev,
            {
              id: makeClientMsgId(),
              from: "system",
              text: "Your advisor declined the request.",
              ts: Date.now(),
            },
          ]);
          return;
        }
      };

      ws.onclose = () => {
        stopPing();
        if (seq !== wsConnectSeqRef.current) return;
        setWsStatus("disconnected");
        wsAttemptRef.current += 1;
        scheduleReconnect();
      };

      ws.onerror = () => {
        if (seq !== wsConnectSeqRef.current) return;
        setWsError("Realtime connection error");
      };
    } catch (e: any) {
      setWsStatus("disconnected");
      setWsError(clean(e?.message) || "Realtime connection failed");
      wsAttemptRef.current += 1;
      scheduleReconnect();
    }
  }, [
    avatarId,
    avatarSrc,
    displayName,
    greetingName,
    refreshAuth,
    refreshRosterFromDb,
    scheduleReconnect,
    scrollToBottom,
    selectedDoctor?.id,
    startPing,
    stopPing,
    stopReconnectTimer,
  ]);

  useEffect(() => {
    connectWsRef.current = connectWs;
  }, [connectWs]);

  useEffect(() => {
    const shouldRunRealtime = effectiveTab === "live";
    wsRunRef.current = shouldRunRealtime;

    const currentAuth = readPortalAuth();
    if (shouldRunRealtime && clean(currentAuth.ptPk)) {
      void connectWs();
    }

    return () => {
      wsRunRef.current = false;
      closeWs();
    };
  }, [effectiveTab, connectWs, closeWs]);

  useEffect(() => {
    return () => {
      stopReconnectTimer();
      stopPing();
      stopPresencePoll();
    };
  }, [stopPing, stopPresencePoll, stopReconnectTimer]);

  useEffect(() => {
    if (!selectedDoctorId && onlineDoctors.length) {
      setSelectedDoctorId(String(onlineDoctors[0].id));
    }
  }, [onlineDoctors, selectedDoctorId]);

  const sendLive = async () => {
    const text = clean(liveText);
    const activeRoom = clean(roomIdRef.current || roomId);
    const isErhRoom = activeRoom.toLowerCase().startsWith("erh:");
    if (!text || (!selectedDoctor && !isErhRoom)) return;

    const currentAuth = refreshAuth();
    if (!currentAuth.ptPk || !currentAuth.token) {
      setLiveMsgs((prev) => [
        ...prev,
        {
          id: makeClientMsgId(),
          from: "system",
          text: "Your session is missing login data. Please sign in again.",
          ts: Date.now(),
        },
      ]);
      return;
    }

    const rid =
      activeRoom ||
      (selectedDoctor
        ? makeRoomIdFromPk(currentAuth.ptPk, selectedDoctor.id)
        : "");
    if (!rid) return;

    if (!roomId) {
      setRoomId(rid);
      roomIdRef.current = rid;
    }

    const newMsg: LiveMsg = {
      id: makeClientMsgId(),
      from: "pt",
      text,
      ts: Date.now(),
    };

    setLiveMsgs((prev) => [...prev, newMsg]);
    setLiveText("");

    const outgoingText =
      translateLanguage !== "English"
        ? await translateText(text, "English")
        : text;

    try {
      const payload = {
        type: "chat.message",
        text: outgoingText || text,
        doctor_id: selectedDoctor?.id || undefined,
        to_doctor_id: selectedDoctor ? String(selectedDoctor.id) : undefined,
        room_id: rid,
        from: "pt",
        from_role: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
        from_pt_id: clean(currentAuth.ptPk),
        pt_id: clean(currentAuth.ptPk),
        pt_pk: clean(currentAuth.ptPk),
        pt_code: clean(currentAuth.ptCode),
        pt_role: clean(currentAuth.ptRole) || inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
        audience: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
        role: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
        portal_role: "pt",
        pt_identity_key: makePtPresenceKey(currentAuth.orgCode, currentAuth.ptCode, currentAuth.ptPk, currentAuth.ptRole),
        org_code: currentAuth.orgCode,
        display_name:
          clean(displayName) || clean(currentAuth.displayName) || greetingName,
      };

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(payload));
      } else {
        await api.post("/realtime/send", payload);
      }
    } catch {
      setLiveMsgs((prev) => [
        ...prev,
        {
          id: makeClientMsgId(),
          from: "system",
          text: "Message could not be sent right now.",
          ts: Date.now(),
        },
      ]);
    } finally {
      window.requestAnimationFrame(() =>
        scrollToBottom(liveScrollBoxRef.current, liveEndRef.current, true),
      );
    }
  };

  const sendInvite = async (mode: InviteMode) => {
    if (!selectedDoctor) return;

    const currentAuth = refreshAuth();
    if (!currentAuth.ptPk || !currentAuth.token) {
      setLiveMsgs((prev) => [
        ...prev,
        {
          id: makeClientMsgId(),
          from: "system",
          text: "Your session is missing login data. Please sign in again.",
          ts: Date.now(),
        },
      ]);
      return;
    }

    const nextRoomId =
      roomId || makeRoomIdFromPk(currentAuth.ptPk, selectedDoctor.id);
    setInviteMode(mode);
    inviteModeRef.current = mode;
    setRoomId(nextRoomId);
    roomIdRef.current = nextRoomId;
    setShowVideoRoom(false);

    try {
      const payload = {
        type: "invite.create",
        to_doctor_id: String(selectedDoctor.id),
        doctor_id: selectedDoctor.id,
        room_id: nextRoomId,
        mode,
        invite_mode: mode,
        chat_or_video: mode,
        is_video: mode === "video",
        from_pt_id: clean(currentAuth.ptPk),
        pt_id: clean(currentAuth.ptPk),
        pt_pk: clean(currentAuth.ptPk),
        pt_code: clean(currentAuth.ptCode),
        pt_role: clean(currentAuth.ptRole) || inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
        audience: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
        role: inferPtAudience(currentAuth.ptRole, currentAuth.ptCode),
        portal_role: "pt",
        pt_identity_key: makePtPresenceKey(currentAuth.orgCode, currentAuth.ptCode, currentAuth.ptPk, currentAuth.ptRole),
        org_code: currentAuth.orgCode,
        display_name:
          clean(displayName) || clean(currentAuth.displayName) || greetingName,
        avatar_id: avatarId,
        avatar_src: avatarSrc,
      };

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(payload));
      } else {
        await api.post("/realtime/invite", payload);
      }

      setLiveMsgs((prev) => [
        ...prev,
        {
          id: makeClientMsgId(),
          from: "system",
          text:
            mode === "video"
              ? "Video invitation sent to the advisor."
              : "Chat invitation sent to the advisor.",
          ts: Date.now(),
        },
      ]);

      window.requestAnimationFrame(() =>
        scrollToBottom(liveScrollBoxRef.current, liveEndRef.current, true),
      );
    } catch {
      setLiveMsgs((prev) => [
        ...prev,
        {
          id: makeClientMsgId(),
          from: "system",
          text: "Could not send invitation right now.",
          ts: Date.now(),
        },
      ]);
    }
  };

  const openDoctorProfile = (doctor: DoctorLite) => {
    setDoctorProfileDoc(doctor);
    setDoctorProfileOpen(true);
  };

  const onLogout = () => {
    [
      "pt_access_token",
      "pt_token",
      "access_token",
      "token",
      "authToken",
      "auth_token",
      "pt_code",
      "parent_teacher_id",
      "parent_teacher_code",
      "pt_id",
      "pt_pk",
      "pt_user_pk",
      "user_id",
      "pt_role",
      "role",
      "user_role",
      "org_code",
      "organization_code",
      "school_code",
      "org",
      "org_id",
      "organization_id",
      "school_org_id",
      "school_id",
      "pt_avatar_id",
      "avatar_id",
      "pt_avatar_src",
      "avatar_src",
      "display_name",
      "pt_display_name",
      "name",
      caseySessionStorageKey,
    ].forEach(removeFromBoth);

    setAiConversationState({});
    navigate("/pt-login");
  };

  return (
    <div className="pt-portal-page">
      <div className="pt-portal-shell">
        <div
          className="pt-portal-header"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "18px 20px",
            background:
              "linear-gradient(135deg, rgba(255,255,255,.96), rgba(255,255,255,.88))",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <img
            src={drIsInLogo}
            alt="Doctor Is In"
            style={{ width: 58, height: 58, objectFit: "contain" }}
          />

          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 900, fontSize: 26, lineHeight: 1.05 }}>
              {portalTitle}
            </div>
            <div style={{ opacity: 0.78, marginTop: 4 }}>
              {portalSubtitle}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={clearCaseyChat}
              disabled={aiBusy}
              style={{
                ...subtleBtn,
                color: "#c63f3f",
                background: aiBusy ? "#f1f1f1" : "rgba(255,255,255,0.88)",
                cursor: aiBusy ? "default" : "pointer",
              }}
              title="Clear this Casey conversation and start fresh"
            >
              Clear Chat
            </button>
            <button
              type="button"
              onClick={() => setGalleryOpen(true)}
              style={subtleBtn}
            >
              Choose Avatar
            </button>
            <button
              type="button"
              onClick={onLogout}
              style={{ ...subtleBtn, background: "#fff4f4" }}
            >
              Log Out
            </button>
          </div>
        </div>

        <div
          style={{
            margin: "16px 18px 0",
            background: "linear-gradient(135deg, rgba(255,255,255,0.94), rgba(255,255,255,0.84))",
            border: "1px solid rgba(255,255,255,0.65)",
            borderRadius: 18,
            padding: "18px 22px",
            boxShadow: "0 12px 34px rgba(0,0,0,0.10)",
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 300px",
            gap: 18,
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: "0 0 6px", fontSize: 30, fontWeight: 900 }}>Hi {greetingName} 👋</h2>
            <div style={{ opacity: 0.9, lineHeight: 1.55, fontWeight: 650 }}>
              Welcome to the <b>Doctor Is In App™</b> {portalTitle}.
              <br />
              {welcomeCopy}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 10,
              minHeight: 126,
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.88)",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 18,
                padding: "12px 14px",
                boxShadow: "0 10px 26px rgba(0,0,0,0.08)",
                maxWidth: 168,
                fontWeight: 800,
                lineHeight: 1.35,
              }}
            >
              <div style={{ fontSize: 14 }}>Hi! I’m Casey.</div>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>Let’s support your student together.</div>
            </div>
            <img
              src={caseyImage}
              alt="Casey, AI Wellness Advisor"
              style={{
                height: 170,
                width: "auto",
                objectFit: "contain",
                filter: "drop-shadow(0 14px 18px rgba(0,0,0,0.18))",
              }}
            />
          </div>
        </div>

        <div className="pt-portal-body">
          <aside className="pt-sidebar">
            <div className="pt-sidebar-scroll">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <AvatarCircle
                  src={avatarSrc || ""}
                  fallback={ptFallback}
                  alt="Selected avatar"
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: 18 }}>
                    {greetingName}
                  </div>
                  <div style={{ opacity: 0.72, fontSize: 13 }}>
                    {ptRoleLabel} •{" "}
                    {clean(ptCode) || clean(ptPk) || "No ID"}
                  </div>
                </div>
              </div>

              {sessionWarning ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: "#fff4da",
                    border: "1px solid #f0d596",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {sessionWarning}
                </div>
              ) : null}


              <div style={{ marginTop: 18 }}>
                <AuraSosLauncher
                  role={ptAudience}
                  compact
                  onActivate={activatePtAuraSos}
                />
              </div>

              {ptAudience === "teacher" ? (
                <div style={{ marginTop: 18, borderRadius: 16, padding: 12, background: "rgba(239,246,255,.92)", border: "1px solid #bfdbfe" }}>
                  <div style={{ fontWeight: 950 }}>Teacher Resources</div>
                  <div style={{ marginTop: 5, fontSize: 12, lineHeight: 1.45, opacity: .78 }}>
                    Lesson plans, posters, classroom guides, and approved school wellness downloads will live here.
                  </div>
                  <button type="button" style={{ ...subtleBtn, marginTop: 10, width: "100%" }} onClick={() => navigate("/teacher/tips")}>
                    Open Teacher Resources
                  </button>
                </div>
              ) : (
                <div style={{ marginTop: 18, borderRadius: 16, padding: 12, background: "rgba(240,253,244,.92)", border: "1px solid #bbf7d0" }}>
                  <div style={{ fontWeight: 950 }}>Family Support</div>
                  <div style={{ marginTop: 5, fontSize: 12, lineHeight: 1.45, opacity: .78 }}>
                    Casey Academy provides health and wellness education for parents and families.
                  </div>
                  <button type="button" style={{ ...subtleBtn, marginTop: 10, width: "100%" }} onClick={() => navigate("/parent/tips")}>
                    Open Casey Academy
                  </button>
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <label
                  style={{ display: "block", fontWeight: 800, marginBottom: 6 }}
                >
                  Display name
                </label>
                <input
                  className="pt-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                />
              </div>

              <div style={{ marginTop: 18 }}>
                <div style={{ fontWeight: 900, marginBottom: 10 }}>
                  Choose a view
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setTab("ai")}
                    style={{
                      ...subtleBtn,
                      background:
                        effectiveTab === "ai" ? "#eaf6ff" : "rgba(255,255,255,0.9)",
                      borderColor:
                        effectiveTab === "ai" ? "#86bdf5" : "rgba(0,0,0,0.12)",
                      flex: 1,
                    }}
                  >
                    Casey AI
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab("live")}
                    style={{
                      ...subtleBtn,
                      background:
                        effectiveTab === "live" ? "#eefaf0" : "rgba(255,255,255,0.9)",
                      borderColor:
                        effectiveTab === "live" ? "#89d19b" : "rgba(0,0,0,0.12)",
                      flex: 1,
                    }}
                  >
                    Live Advisor
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 22 }}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>
                  Casey Academy
                </div>
                <select
                  className="pt-input"
                  value={tipCategory}
                  onChange={(e) =>
                    setTipCategory(e.target.value as TipCategory)
                  }
                  aria-label="Choose Casey Academy category"
                >
                  {PT_TIP_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>

                <div
                  className="scroll-y flex-col-can-scroll"
                  style={{
                    display: "grid",
                    gap: 8,
                    maxHeight: 220,
                    marginTop: 10,
                    paddingRight: 4,
                  }}
                >
                  {tipsBusy ? (
                    <div
                      style={{
                        border: "1px solid rgba(0,0,0,0.10)",
                        borderRadius: 14,
                        padding: 12,
                        background: "rgba(255,255,255,0.82)",
                        opacity: 0.75,
                        fontWeight: 700,
                      }}
                    >
                      Loading tips...
                    </div>
                  ) : tipsError ? (
                    <div
                      style={{
                        border: "1px solid #efb3b3",
                        borderRadius: 14,
                        padding: 12,
                        background: "#fff0f0",
                        color: "#7a2323",
                        fontWeight: 700,
                      }}
                    >
                      {tipsError}
                    </div>
                  ) : tips.length ? (
                    tips.slice(0, 8).map((tip) => {
                      const tipId = String(tip.id);
                      const expanded = expandedTipId === tipId;
                      const body = clean(tip.body);
                      const preview =
                        body.length > 110 ? `${body.slice(0, 110)}...` : body;

                      return (
                        <div
                          key={tipId}
                          style={{
                            border: "1px solid rgba(0,0,0,0.10)",
                            borderRadius: 14,
                            background: "rgba(255,255,255,0.9)",
                            padding: 10,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedTipId(expanded ? "" : tipId)
                            }
                            style={{
                              all: "unset",
                              cursor: "pointer",
                              display: "block",
                              width: "100%",
                            }}
                          >
                            <div style={{ fontWeight: 900, lineHeight: 1.2 }}>
                              {clean(tip.title) || "Wellness tip"}
                            </div>
                            {body ? (
                              <div
                                style={{
                                  marginTop: 5,
                                  fontSize: 13,
                                  opacity: 0.78,
                                }}
                              >
                                {expanded ? body : preview}
                              </div>
                            ) : null}
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 12,
                                fontWeight: 900,
                                opacity: 0.58,
                              }}
                            >
                              {expanded ? "Show less" : "Show more"}
                            </div>
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        border: "1px dashed rgba(0,0,0,0.16)",
                        borderRadius: 14,
                        padding: 12,
                        background: "rgba(255,255,255,0.8)",
                        opacity: 0.78,
                      }}
                    >
                      No Casey Academy material found for this category.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 22 }}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>Advisors</div>
                <div style={{ opacity: 0.72, fontSize: 13, marginBottom: 8 }}>
                  {doctorSource}
                </div>

                <div
                  className="scroll-y flex-col-can-scroll"
                  style={{
                    display: "grid",
                    gap: 8,
                    maxHeight: 420,
                    paddingRight: 4,
                  }}
                >
                  {onlineDoctors.length ? (
                    onlineDoctors.map((d) => {
                      const isSelected =
                        String(d.id) === String(selectedDoctorId);
                      const badge = d.is_online
                        ? d.is_available
                          ? "Online"
                          : "Busy"
                        : "Offline";

                      return (
                        <div
                          key={d.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedDoctorId(String(d.id))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedDoctorId(String(d.id));
                            }
                          }}
                          style={{
                            textAlign: "left",
                            width: "100%",
                            border: isSelected
                              ? "1px solid #7ab4ff"
                              : "1px solid rgba(0,0,0,0.10)",
                            borderRadius: 16,
                            background: isSelected
                              ? "#eef6ff"
                              : "rgba(255,255,255,0.9)",
                            padding: 10,
                            cursor: "pointer",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: 10,
                              alignItems: "center",
                            }}
                          >
                            <AvatarCircle
                              src={d.photo_url || ""}
                              fallback="D"
                              alt={d.name || "Advisor"}
                            />
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                style={{ fontWeight: 900, lineHeight: 1.15 }}
                              >
                                {d.name}
                              </div>
                              <div
                                style={{
                                  opacity: 0.75,
                                  fontSize: 13,
                                  marginTop: 2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {d.medical_field ||
                                  d.specialties ||
                                  "General Medicine"}
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 900,
                                padding: "6px 8px",
                                borderRadius: 999,
                                background: d.is_online
                                  ? d.is_available
                                    ? "#e9f8ee"
                                    : "#fff4dd"
                                  : "#f1f1f1",
                              }}
                            >
                              {badge}
                            </div>
                          </div>

                          <div
                            style={{ display: "flex", gap: 8, marginTop: 10 }}
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDoctorProfile(d);
                              }}
                              style={{
                                ...subtleBtn,
                                padding: "7px 10px",
                                fontSize: 13,
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        border: "1px dashed rgba(0,0,0,0.16)",
                        borderRadius: 16,
                        padding: 16,
                        background: "rgba(255,255,255,0.8)",
                        opacity: 0.78,
                      }}
                    >
                      No advisors found yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>

          <main className="pt-main">
            <div className="pt-main-content">
              {effectiveTab === "ai" ? (
                <>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <AvatarCircle
                      src={caseyImage}
                      fallback="C"
                      alt="Casey"
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 900, fontSize: 22 }}>
                        Talk with Casey
                      </div>
                      <div style={{ opacity: 0.78 }}>
                        Casey is here to help with school, family, stress, and
                        emotional support.
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <select
                          value={translateLanguage}
                          onChange={(e) => setTranslateLanguage(e.target.value)}
                          dir={isRtlLanguage(translateLanguage) ? "rtl" : "ltr"}
                          aria-label="Casey chat language"
                        >
                          {LANGUAGE_OPTIONS.map((language) => (
                            <option
                              key={language.code}
                              value={language.value}
                              dir={language.rtl ? "rtl" : "ltr"}
                            >
                              {language.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {systemNotice ? (
                    <div
                      style={{
                        marginTop: 14,
                        background: "#fff4da",
                        border: "1px solid #f0d596",
                        borderRadius: 14,
                        padding: "12px 14px",
                        fontWeight: 700,
                      }}
                    >
                      {systemNotice}
                    </div>
                  ) : null}

                  <div ref={aiScrollBoxRef} className="pt-chat-window">
                    {aiMessages.length === 0 ? (
                      <div
                        style={{
                          height: "100%",
                          display: "grid",
                          placeItems: "center",
                          color: "rgba(0,0,0,0.55)",
                          textAlign: "center",
                          padding: 20,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 900, fontSize: 22 }}>
                            Hi, I’m Casey.
                          </div>
                          <div style={{ marginTop: 8 }}>
                            {ptAudience === "teacher"
                          ? "I’m here to help you support your students and classroom. What would you like help with today?"
                          : "I’m here to help you support your child. What would you like help with today?"}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 12 }}>
                        {aiMessages.map((m) => {
                          const mine = m.from === "pt";
                          return (
                            <div
                              key={m.id}
                              style={{
                                display: "flex",
                                justifyContent: mine
                                  ? "flex-end"
                                  : "flex-start",
                              }}
                            >
                              <div
                                style={{
                                  maxWidth: "78%",
                                  borderRadius: 18,
                                  padding: "12px 14px",
                                  background: mine ? "#dff1ff" : "#ffffff",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  boxShadow: "0 5px 16px rgba(0,0,0,0.06)",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {m.text}
                              </div>
                            </div>
                          );
                        })}
                        <div ref={aiEndRef} />
                      </div>
                    )}
                  </div>

                  <div className="pt-chat-input-row">
                    <input
                      className="pt-input"
                      value={aiText}
                      onChange={(e) => setAiText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          void sendAi();
                        }
                      }}
                      placeholder={
                        aiBusy
                          ? "Casey is typing..."
                          : "Type your message to Casey..."
                      }
                      style={{ flex: 1 }}
                      dir={isRtlLanguage(translateLanguage) ? "rtl" : "ltr"}
                    />
                    <button
                      type="button"
                      onClick={() => void sendAi()}
                      disabled={aiBusy}
                      style={{
                        ...subtleBtn,
                        minWidth: 110,
                        background: aiBusy ? "#f1f1f1" : "#e9f5ff",
                        cursor: aiBusy ? "default" : "pointer",
                      }}
                    >
                      {aiBusy ? "..." : "Send"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 900, fontSize: 22 }}>
                        Live Advisor
                      </div>
                      <div style={{ opacity: 0.78 }}>
                        Connect with an available advisor by chat or video.
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <select
                          value={translateLanguage}
                          onChange={(e) => setTranslateLanguage(e.target.value)}
                          aria-label="Live advisor chat language"
                          dir={isRtlLanguage(translateLanguage) ? "rtl" : "ltr"}
                        >
                          {LANGUAGE_OPTIONS.map((language) => (
                            <option
                              key={language.code}
                              value={language.value}
                              dir={language.rtl ? "rtl" : "ltr"}
                            >
                              {language.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div
                      style={{
                        fontWeight: 900,
                        padding: "8px 12px",
                        borderRadius: 999,
                        background:
                          wsStatus === "connected"
                            ? "#eaf8ec"
                            : wsStatus === "connecting"
                              ? "#fff5dc"
                              : "#f2f2f2",
                      }}
                    >
                      {liveStatusLabel || "Idle"}
                    </div>
                  </div>

                  {wsError ? (
                    <div
                      style={{
                        marginTop: 12,
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "#fff0f0",
                        border: "1px solid #efb3b3",
                        color: "#7a2323",
                        fontWeight: 700,
                      }}
                    >
                      {wsError}
                    </div>
                  ) : null}

                  <div ref={liveScrollBoxRef} className="pt-chat-window">
                    {liveMsgs.length === 0 ? (
                      <div
                        style={{
                          height: "100%",
                          display: "grid",
                          placeItems: "center",
                          color: "rgba(0,0,0,0.55)",
                          textAlign: "center",
                          padding: 20,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 900, fontSize: 22 }}>
                            Select an advisor and start a live connection.
                          </div>
                          <div style={{ marginTop: 8 }}>
                            Use chat for quick help or video for face-to-face
                            support.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 12 }}>
                        {liveMsgs.map((m) => {
                          const mine = m.from === "pt";
                          return (
                            <div
                              key={m.id}
                              style={{
                                display: "flex",
                                justifyContent:
                                  m.from === "system"
                                    ? "center"
                                    : mine
                                      ? "flex-end"
                                      : "flex-start",
                              }}
                            >
                              <div
                                style={{
                                  maxWidth: m.from === "system" ? "88%" : "78%",
                                  borderRadius: 18,
                                  padding: "12px 14px",
                                  background:
                                    m.from === "system"
                                      ? "#fff9df"
                                      : mine
                                        ? "#dff1ff"
                                        : "#ffffff",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  boxShadow: "0 5px 16px rgba(0,0,0,0.06)",
                                  whiteSpace: "pre-wrap",
                                  textAlign:
                                    m.from === "system" ? "center" : "left",
                                }}
                              >
                                {m.text}
                              </div>
                            </div>
                          );
                        })}
                        <div ref={liveEndRef} />
                      </div>
                    )}
                  </div>

                  <div
                    className="pt-chat-input-row"
                    style={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    <button
                      type="button"
                      onClick={() => void sendInvite("chat")}
                      disabled={!selectedDoctor}
                      style={{
                        ...subtleBtn,
                        background: selectedDoctor ? "#eaf6ff" : "#f1f1f1",
                        minWidth: 120,
                      }}
                    >
                      Invite to Chat
                    </button>
                    <button
                      type="button"
                      onClick={() => void sendInvite("video")}
                      disabled={!selectedDoctor}
                      style={{
                        ...subtleBtn,
                        background: selectedDoctor ? "#eefaf0" : "#f1f1f1",
                        minWidth: 120,
                      }}
                    >
                      Invite to Video
                    </button>

                    <input
                      className="pt-input"
                      value={liveText}
                      onChange={(e) => setLiveText(e.target.value)}
                      dir={isRtlLanguage(translateLanguage) ? "rtl" : "ltr"}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          void sendLive();
                        }
                      }}
                      placeholder={
                        clean(roomIdRef.current || roomId).toLowerCase().startsWith("erh:")
                          ? "Message the attending advisor..."
                          : selectedDoctor
                            ? `Message ${selectedDoctor.name}...`
                            : "Select an advisor first"
                      }
                      style={{ flex: 1, minWidth: 260 }}
                    />
                    <button
                      type="button"
                      onClick={() => void sendLive()}
                      disabled={
                        !clean(liveText) ||
                        (!selectedDoctor &&
                          !clean(roomIdRef.current || roomId)
                            .toLowerCase()
                            .startsWith("erh:"))
                      }
                      style={{
                        ...subtleBtn,
                        background:
                          clean(liveText) &&
                          (selectedDoctor ||
                            clean(roomIdRef.current || roomId)
                              .toLowerCase()
                              .startsWith("erh:"))
                            ? "#e9f5ff"
                            : "#f1f1f1",
                        minWidth: 100,
                      }}
                    >
                      Send
                    </button>
                  </div>

                  {showVideoRoom && selectedDoctor ? (
                    <div style={{ marginTop: 16 }}>
                      <VideoRoomAny
                        ws={wsRef.current}
                        roomId={
                          roomId ||
                          makeRoomIdFromPk(
                            readPortalAuth().ptPk,
                            selectedDoctor.id,
                          )
                        }
                        room_id={
                          roomId ||
                          makeRoomIdFromPk(
                            readPortalAuth().ptPk,
                            selectedDoctor.id,
                          )
                        }
                        role={videoRole}
                        onHangup={() => setShowVideoRoom(false)}
                        onEnd={() => setShowVideoRoom(false)}
                      />
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </main>
        </div>

        <div
          className="pt-footer"
          style={{
            borderTop: "1px solid rgba(0,0,0,0.06)",
            padding: "12px 20px",
            background: "rgba(255,255,255,0.8)",
            fontSize: 13,
            opacity: 0.78,
            textAlign: "center",
          }}
        >
          © {year} Doctor Is In
        </div>
      </div>

      <AvatarGalleryModal
        open={galleryOpen}
        title="Choose your avatar"
        avatars={AVATARS}
        selectedId={avatarId}
        onSelect={(avatar) => {
          setAvatarId(clean((avatar as any)?.id));
          setAvatarSrc(clean((avatar as any)?.src));
          setGalleryOpen(false);
        }}
        onClose={() => setGalleryOpen(false)}
        onClear={() => {
          setAvatarId("");
          setAvatarSrc("");
          setGalleryOpen(false);
        }}
      />

      <DoctorProfileModal
        open={doctorProfileOpen}
        doctor={doctorProfileDoc}
        onClose={() => setDoctorProfileOpen(false)}
      />
    </div>
  );
}